import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  Portal,
  Tooltip,
  useDisclosure,
  useToast,
  Input,
  Popover,
  PopoverContent,
  PopoverBody,
  PopoverAnchor,
  HStack,
} from '@chakra-ui/react';
import {
  faArrows,
  faCheck,
  faChevronDown,
  faCopy,
  faEdit,
  faPen,
  faPlus,
  faTimes,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';

import { useFlag } from '@unleash/proxy-client-react';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import ConfirmDeletionModal from '../common/ConfirmDeletionModal';
import { DbIdsPerModel, useViewer } from '../common/ForgeViewer';
import SparkelIcon from '../common/icon/SparkelIcon';
import {
  Panel,
  PanelActionButtons,
  PanelContent,
  PanelHeader,
  PanelTitle,
} from '../common/Panel';
import { useViewerMode, ViewerMode } from '../common/viewer-mode';
import PlaceholderText from '../text/PlaceholderText';
import { useProjectHistory } from '../project/project-history/hooks/useProjectHistory';
import { useDiffing } from '../project/project-history/hooks/useDiffing';
import { useSheetViewerV2ContextMenuContext } from '../viewers/SheetsViewerV2/SheetsViewerV2.context';
import { ENABLE_APS_SHEET_VIEWER_FLAG } from '../viewers/SheetsViewerV2/SheetsViewerV2.config';
import ShapeItem, { DragData, ShapeItemDiff, ShapeNodeType } from './ShapeItem';
import { getDisplayName } from './folder-hierarchy-utils';
import { AutoshapePopover } from './AutoshapePopover';
import ShapesFolder from './ShapesFolder';
import {
  buildFolderHierarchy,
  flattenHierarchyForVirtualization,
  HierarchyNode,
  VirtualFolderNode,
  isVirtualFolderNode,
  findNodeByPath,
  collectAllShapesInHierarchy,
  getParentPath,
} from './folder-hierarchy-utils';
import { useEnabledSheetViewer } from 'src/components/viewers/common/hooks/useEnabledSheetViewer';
import { DrawingMode } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/ShapesDrawingTool';
import {
  AutoShapeDeepFragment,
  BulkDeleteShapesDocument,
  BulkDeleteSheetShapesDocument,
  DeleteShapeDocument,
  DeleteSheetShapeDocument,
  EventResource,
  EventType,
  GetProjectSettingsDocument,
  ShapeDeepFragment,
  ShapeFolderDeepFragment,
  SheetShapeDeepFragment,
  UpdateShapeDocument,
  UpdateSheetShapeDocument,
  BulkDeleteSparkelPropertiesDocument,
  GetSparkelPropertiesForProjectDocument,
  GetSparkelPropertiesForProjectQuery,
} from 'src/gql/graphql';
import { useActiveModels } from 'src/hooks/active-models';
import { useAutoShapes } from 'src/hooks/auto-shapes';
import { useBimShapes } from 'src/hooks/bim-shapes';
import { useLocalStorage } from 'src/hooks/local-storage';
import { useShapeFolders } from 'src/hooks/shape-folders';
import { useSheetShapes } from 'src/hooks/sheet-shapes';
import { useForgeContextMenuContext } from 'src/services/viewer/ForgeContextMenu';
import { useSelection } from 'src/services/viewer/selection';
import { useSheetContextMenuContext } from 'src/services/viewer/SheetsContextMenu';
import { useVisibility } from 'src/services/viewer/visibility';
import { ChakraColor } from 'src/utils/color-util';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { useEventHistory } from 'src/hooks/event-history';
import { getShapeUrn } from 'src/services/viewer/ShapesManager';
import { useUserTenant } from 'src/services/auth-info';
import { getRoofShapeFromProperties } from 'src/components/orderContractor/pdf-shapes/SheetDrainLineDrawing';
import { useCopyPasteShapes } from 'src/hooks/useCopyPasteShapes';
//

type ShapesPanelProps = {
  readOnly: boolean;
  projectId: string;
};

const SHAPE_ITEM_SIZE = 26;

// eslint-disable-next-line complexity
function ShapesPanel({ readOnly }: ShapesPanelProps) {
  const [, updateShapeMutation] = useMutation(UpdateShapeDocument);

  const [, updateSheetShapeMutation] = useMutation(UpdateSheetShapeDocument);

  const [, deleteShapeMutation] = useMutation(DeleteShapeDocument);

  const [, deleteSheetShapeMutation] = useMutation(DeleteSheetShapeDocument);

  const [, bulkDeleteShapesMutation] = useMutation(BulkDeleteShapesDocument);

  const [, bulkDeleteSheetShapesMutation] = useMutation(
    BulkDeleteSheetShapesDocument
  );

  const enableAutoShapes = useFlag('enable-auto-shapes');
  const enableSheetViewerV2 = useFlag(ENABLE_APS_SHEET_VIEWER_FLAG);
  const enableDrainageLine = useFlag('enable-drainage-line');

  const toast = useToast();
  const { t } = useTranslation(['common', 'project']);
  const { show, hide } = useVisibility();

  const panelRef = useRef<HTMLDivElement>(null);
  const [isRenamePopoverOpen, setIsRenamePopoverOpen] = useState(false);
  const [renameAnchorPos, setRenameAnchorPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [renameTarget, setRenameTarget] = useState<ShapeNodeType | null>(null);
  const [renameInput, setRenameInput] = useState<string>('');
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  const openRenamePopover = useCallback(
    (node: ShapeNodeType, coords?: { x: number; y: number }) => {
      // If multiple shapes are selected, use the first selected shape's name as initial value
      const multiSelected = selectedShapesRef.current?.length > 1;
      const initialName = multiSelected
        ? selectedShapesRef.current[0]?.name ?? node.name ?? ''
        : node.name ?? '';

      setRenameTarget(node);
      setRenameInput(initialName);
      if (coords) {
        setRenameAnchorPos(coords);
      }
      setIsRenamePopoverOpen(true);
      // Focus and select will be handled after open via setTimeout to ensure render
      setTimeout(() => {
        if (renameInputRef.current) {
          renameInputRef.current.focus();
          renameInputRef.current.select();
        }
      }, 0);
    },
    []
  );

  const closeRenamePopover = useCallback(() => {
    setIsRenamePopoverOpen(false);
    setRenameTarget(null);
    setRenameInput('');
  }, []);
  const [shapesPanelHeight, setShapesPanelHeight] = useLocalStorage<
    number | undefined
  >('shapes-panel-height', undefined);
  const [isHoveredOutside, setIsHoveredOutside] = useState(false);

  const {
    shapes: { data: shapes3d },
    drawing: {
      activate: activate3dDrawing,
      isMovingShapes: isMovingShapes3D,
      setMovingShapes: setMovingShapes3D,
      isCopyingShapes: isCopyingShapes3D,
      setCopyingShapes: setCopyingShapes3D,
      deactivate: deactivate3dDrawing,
      isDrawing: isDrawing3d,
      hasResult: has3dDrawingResult,
      submit: submit3dDrawing,
      editShape: edit3dShape,
    },
  } = useBimShapes();

  const {
    shapes: { data: sheetShapes },
    drawing: {
      activate: activateSheetDrawingV1,
      isMovingShapes: isMovingShapes2D,
      setMovingShapes: setMovingShapes2D,
      isCopyingShapes: isCopyingShapes2D,
      setCopyingShapes: setCopyingShapes2D,
      deactivate: deactivateSheetDrawingV1,
      isDrawing: isDrawingSheetsV1,
      hasResult: hasSheetDrawingResult,
      submit: submitSheetDrawing,
      editShape: editSheetShape,
      isPasting: isPasting2D,
      startPastePreview,
      cancelPastePreview,
    },
  } = useSheetShapes();
  const { setActiveTool, drawing: sheetsViewerV2Drawing } = useSheetViewerV2();
  const { isProtan } = useUserTenant();
  const { setViewerMode, viewerMode } = useViewerMode();

  const isDrawingSheets =
    viewerMode === ViewerMode.SheetsV2 && enableSheetViewerV2
      ? sheetsViewerV2Drawing.isDrawing
      : isDrawingSheetsV1;
  const activateSheetDrawing =
    viewerMode === ViewerMode.SheetsV2 && enableSheetViewerV2
      ? () => {
          setActiveTool(ActiveTool.Drawing);
          // For Protan, default drawing mode to Drain Line if available, otherwise Point
          if (isProtan) {
            sheetsViewerV2Drawing.setDrawingMode(DrawingMode.Point);
          }
        }
      : activateSheetDrawingV1;
  const deactivateSheetDrawing =
    viewerMode === ViewerMode.SheetsV2 && enableSheetViewerV2
      ? () => setActiveTool(ActiveTool.Default)
      : deactivateSheetDrawingV1;

  const {
    autoShapes,
    createAutoShape,
    deleteAutoShape,
    renameAutoShape,
    findNextAvailableAutoShapeName,
  } = useAutoShapes();

  const {
    visibilityManager: { hiddenElements: hiddenBIMElements },
  } = useViewer();

  const enabledSheetViewer = useEnabledSheetViewer();
  const {
    visibilityManager: { hiddenElements: hiddenSheetElements },
    setPageNumber,
    changeActiveSheet,
  } = enabledSheetViewer;

  // Get auto-hidden legends (only available in V2 context)
  const autoHiddenLegends =
    'autoHiddenLegends' in enabledSheetViewer
      ? enabledSheetViewer.autoHiddenLegends
      : {};

  const { projectId, orderId } = useParams();
  // Hotkeys: Symbol Detection quick actions
  useHotkeys(
    'shift+q',
    () => {
      // Trigger opening the Autoshape popover by clicking the "Add" button path
      // We simulate a click on the add button so the popover mounts, then start picking
      try {
        const addButton = document.querySelector(
          'button[aria-label="add-shape-options"]'
        ) as HTMLButtonElement | null;
        if (addButton) {
          addButton.click();
        }
      } catch {
        console.error('[AutoShapes] add-shape-options button not found');
      }
      // Ask popover to jump straight into Symbol Detection settings
      try {
        const ev = new Event('autoshape:open:symbol-detection-2d');
        window.dispatchEvent(ev);
      } catch {
        console.error(
          '[AutoShapes] autoshape:open:symbol-detection-2d not dispatched'
        );
      }
    },
    {
      enabled: enableAutoShapes && viewerMode === ViewerMode.SheetsV2,
    },
    []
  );

  useHotkeys(
    'esc',
    () => {
      try {
        window.dispatchEvent(new Event('symbol-detection:cancel'));
      } catch {
        console.error('[AutoShapes] symbol-detection:cancel not dispatched');
      }
    },
    {
      enabled: enableAutoShapes && viewerMode === ViewerMode.SheetsV2,
    },
    []
  );

  const { activeModels } = useActiveModels(projectId ?? '');

  const {
    activeSheetId,
    pagination: { pageNumber: activeSheetPageNumber },
    sheets,
  } = useSheetViewerV2();

  // Copy/Paste shapes across sheets
  const { clipboardState, hasClipboard, copyShapes, startPasteMode } =
    useCopyPasteShapes({
      sheets,
      activeSheetId,
      pageNumber: activeSheetPageNumber,
    });

  // Start paste preview using the context-based approach
  const handleStartPaste = useCallback(() => {
    if (!clipboardState) return false;

    // Validate target calibration via startPasteMode (shows toast if invalid)
    if (!startPasteMode()) return false;

    // Start the paste preview in the sheet-shapes context
    startPastePreview({
      shapes: clipboardState.shapes,
      sourceCenter: clipboardState.center,
      sourceCalibration: clipboardState.sourceCalibration,
    });

    return true;
  }, [clipboardState, startPasteMode, startPastePreview]);

  // Sparkel properties for determining RoofShapeDbId references
  const [{ data: sparkelPropsData }] = useQuery({
    query: GetSparkelPropertiesForProjectDocument,
    variables: { projectId: projectId ?? '' },
    pause: !projectId || !isProtan,
  });
  const [, bulkDeleteSparkelProps] = useMutation(
    BulkDeleteSparkelPropertiesDocument
  );

  const isSheetShapeNode = (
    node: ShapeNodeType
  ): node is SheetShapeDeepFragment => node.__typename === 'SheetShape';

  const cleanupRoofFlagsForDeletedDrainLines = useCallback(
    async (
      nodes: ShapeNodeType[],
      sparkelProps: GetSparkelPropertiesForProjectQuery | undefined,
      projId: string | undefined,
      isProtanTenant: boolean
    ) => {
      if (
        !isProtanTenant ||
        !sparkelProps?.project?.sparkelProperties ||
        !projId
      ) {
        return;
      }

      const sheetNodes = nodes.filter(isSheetShapeNode);
      const drainLineNodes = sheetNodes.filter((n) => !!n.sheetShapeSplitLine);

      for (const node of drainLineNodes) {
        const roofDbId = getRoofShapeFromProperties(
          sparkelProps,
          node.dbId,
          node.urn
        );

        if (!roofDbId) continue;

        const otherRefs = sparkelProps.project.sparkelProperties.filter(
          (prop) =>
            prop.propertySet === 'RoofShapeDbId' &&
            prop.modelUrn === node.urn &&
            prop.propertyValue === roofDbId.toString() &&
            prop.dbId !== node.dbId
        );

        if (otherRefs.length === 0) {
          await bulkDeleteSparkelProps({
            input: {
              projectId: projId,
              dbIds: [
                {
                  modelUrn: node.urn,
                  dbIds: [roofDbId],
                },
              ],
              thePropertySet: 'Is Tapered Insulation',
            },
          });
        }
      }
    },
    [bulkDeleteSparkelProps]
  );

  const [{ data: getProjectSettings }] = useQuery({
    query: GetProjectSettingsDocument,
    variables: { projectId: projectId ?? '' },
  });

  const globalOffset =
    getProjectSettings?.project?.settings?.viewerGlobalOffset ?? null;

  const hasDrawingResult = has3dDrawingResult || hasSheetDrawingResult;

  useHotkeys(
    'esc',
    () => {
      setMovingShapes3D(false);
      setCopyingShapes3D(false);
      setMovingShapes2D(false);
      setCopyingShapes2D(false);
    },
    {
      enabled:
        isMovingShapes3D ||
        isCopyingShapes3D ||
        isMovingShapes2D ||
        isCopyingShapes2D,
    }
  );

  useHotkeys(
    'shift+d',
    () => {
      viewerMode === ViewerMode.Models
        ? setCopyingShapes3D(true)
        : setCopyingShapes2D(true);
    },
    {
      enabled:
        !isCopyingShapes3D &&
        !isMovingShapes3D &&
        !isMovingShapes2D &&
        !isCopyingShapes2D,
    }
  );

  // Copy selected sheet shapes to clipboard (Ctrl/Cmd+C)
  useHotkeys(
    'mod+c',
    () => {
      // Get selected sheet shapes from the ref
      const selectedSheetShapesArray = selectedShapesRef.current?.filter(
        (shape): shape is SheetShapeDeepFragment =>
          shape.__typename === 'SheetShape'
      );
      if (selectedSheetShapesArray && selectedSheetShapesArray.length > 0) {
        copyShapes(selectedSheetShapesArray);
      }
    },
    {
      enabled:
        viewerMode !== ViewerMode.Models &&
        !isDrawingSheets &&
        !isPasting2D &&
        !isMovingShapes2D &&
        !isCopyingShapes2D,
      preventDefault: true,
    },
    [
      copyShapes,
      viewerMode,
      isDrawingSheets,
      isPasting2D,
      isMovingShapes2D,
      isCopyingShapes2D,
    ]
  );

  // Paste shapes from clipboard (Ctrl/Cmd+V)
  useHotkeys(
    'mod+v',
    () => {
      handleStartPaste();
    },
    {
      enabled:
        hasClipboard &&
        viewerMode !== ViewerMode.Models &&
        !isDrawingSheets &&
        !isPasting2D &&
        !isMovingShapes2D &&
        !isCopyingShapes2D,
      preventDefault: true,
    },
    [
      handleStartPaste,
      hasClipboard,
      viewerMode,
      isDrawingSheets,
      isPasting2D,
      isMovingShapes2D,
      isCopyingShapes2D,
    ]
  );

  // Cancel paste mode with ESC
  useHotkeys(
    'esc',
    () => {
      cancelPastePreview();
    },
    {
      enabled: isPasting2D,
    },
    [cancelPastePreview, isPasting2D]
  );

  const {
    data: shapeFolders,
    moveShapesToFolder,
    removeShapesFromFolder,
    renameFolder,
    bulkRenameFolders,
    selectedFolder,
    setSelectedFolder,
    deleteFolder,
    createFolder,
    findNextAvailableFolderName,
  } = useShapeFolders();

  const {
    setSelectedDbIds,
    deselectDbIds,
    selectedDbIds,
    selectedShapes: selected3dShapes,
    selectedSheetShapes,
  } = useSelection();

  const { logEvent } = useEventHistory(projectId ?? '');
  const { diff, isHistoryDrawerOpen, referenceState } = useProjectHistory();
  const { getEventType, getAttributeDiff } = useDiffing();

  const selectedDbIdsInternalRef = useRef(selectedDbIds);

  useEffect(() => {
    selectedDbIdsInternalRef.current = selectedDbIds;
  }, [selectedDbIds]);

  const shapes = useMemo(() => {
    if (shapes3d && sheetShapes) {
      return [...shapes3d, ...sheetShapes];
    } else {
      return [];
    }
  }, [shapes3d, sheetShapes]);

  const shapesRef = useRef(shapes);
  useEffect(() => {
    shapesRef.current = shapes;
  }, [shapes]);

  const shapesWithNoFolder = useMemo(
    () =>
      shapes
        .filter((shape) => !shape.folder && !shape.autoShapeId)
        .sort((a, b) => compareShapeNameWithNumber(a.name, b.name)),
    [shapes]
  );

  const selectedShapes = useMemo(
    () =>
      shapes.filter((shape) => {
        const urn = shape.urn;
        const selectedDbIds = selected3dShapes[urn] || selectedSheetShapes[urn];
        return selectedDbIds?.includes(shape.dbId);
      }),
    [shapes, selected3dShapes, selectedSheetShapes]
  );

  const selectedShapesRef = useRef(selectedShapes);
  useEffect(() => {
    selectedShapesRef.current = selectedShapes;
  }, [selectedShapes]);

  /**
   * The path of the currently active (selected) folder.
   * Used for placing new shapes/autoshapes in the correct folder.
   */
  const activeFolderPath = useMemo(() => {
    if (!selectedFolder) return null;
    const folder = shapeFolders?.find((f) => f.id === selectedFolder);
    return folder?.name ?? null;
  }, [selectedFolder, shapeFolders]);

  /**
   * Check if all selected shapes have the same immediate parent folder.
   * Returns the common parent folder ID (or null if all are at root level),
   * or undefined if shapes are from different parent folders.
   *
   * When a folder is selected, we return the parent of that folder (so grouping
   * wraps the folder, not creates inside it).
   */
  const commonParentFolderId = useMemo((): string | null | undefined => {
    // If a folder is selected, use the parent of that folder
    if (selectedFolder) {
      const folder = shapeFolders?.find((f) => f.id === selectedFolder);
      if (folder) {
        // Get the parent path of the selected folder
        const parentPath = getParentPath(folder.name);
        if (parentPath === null) {
          // Folder is at root level, so parent is null (root)
          return null;
        }
        // Find the parent folder by path
        const parentFolder = shapeFolders?.find((f) => f.name === parentPath);
        return parentFolder?.id ?? null;
      }
    }

    if (!selectedShapes || selectedShapes.length === 0) {
      return undefined;
    }

    // Get the immediate parent folder ID for each shape
    const parentFolderIds = selectedShapes.map((shape) => shape.folder ?? null);

    // Check if all have the same parent
    const firstParent = parentFolderIds[0];
    const allSameParent = parentFolderIds.every((id) => id === firstParent);

    return allSameParent ? firstParent : undefined;
  }, [selectedShapes, selectedFolder, shapeFolders]);

  /**
   * Check if multiple folders with the same parent are selected via their shapes.
   * This allows grouping multiple sibling folders together.
   */
  const canGroupMultipleFolders = useMemo(() => {
    if (!selectedShapes || selectedShapes.length === 0) return false;

    // Get unique folder IDs from selected shapes
    const uniqueFolderIds = [
      ...new Set(
        selectedShapes
          .map((s) => s.folder)
          .filter((id): id is string => id !== null && id !== undefined)
      ),
    ];

    // Need at least 2 different folders
    if (uniqueFolderIds.length < 2) return false;

    // Get the folders and check they all have the same parent path
    const selectedFolderObjects = uniqueFolderIds
      .map((id) => shapeFolders?.find((f) => f.id === id))
      .filter((f): f is NonNullable<typeof f> => f !== undefined);

    if (selectedFolderObjects.length < 2) return false;

    const parentPaths = selectedFolderObjects.map((f) => getParentPath(f.name));
    return parentPaths.every((p) => p === parentPaths[0]);
  }, [selectedShapes, shapeFolders]);

  /**
   * Check if grouping is allowed for current selection.
   * Only allowed if all selected shapes have the same immediate parent folder,
   * or if a folder is selected (which can always be wrapped),
   * or if multiple folders with the same parent are selected.
   */
  const canGroupSelectedShapes =
    selectedFolder !== null ||
    commonParentFolderId !== undefined ||
    canGroupMultipleFolders;

  // eslint-disable-next-line complexity
  const groupShapesIntoFolder = useCallback(async () => {
    // Case 1: Multiple folders are selected via their shapes - wrap all folders
    // Check this FIRST before single folder case
    const currentSelectedShapes = selectedShapesRef.current;
    if (currentSelectedShapes && currentSelectedShapes.length > 0) {
      // Get unique folder IDs from selected shapes (excluding shapes without folders)
      const uniqueFolderIds = [
        ...new Set(
          currentSelectedShapes
            .map((s) => s.folder)
            .filter((id): id is string => id !== null && id !== undefined)
        ),
      ];

      // Check if we have multiple folders selected
      if (uniqueFolderIds.length > 1) {
        // Get the folders and check they all have the same parent
        const selectedFolderObjects = uniqueFolderIds
          .map((id) => shapeFolders?.find((f) => f.id === id))
          .filter((f): f is NonNullable<typeof f> => f !== undefined);

        if (selectedFolderObjects.length > 1) {
          // Get parent paths for all selected folders
          const parentPaths = selectedFolderObjects.map((f) =>
            getParentPath(f.name)
          );
          const allSameParent = parentPaths.every((p) => p === parentPaths[0]);

          if (allSameParent) {
            // All folders have the same parent - wrap them all into a new group
            const parentPath = parentPaths[0];

            // Create new folder at the common parent level
            const baseName = t('common:shape-folder');
            const fullName = parentPath
              ? `${parentPath}/${baseName}`
              : baseName;
            const newFolderName = findNextAvailableFolderName(fullName);
            const newFolder = await createFolder(newFolderName);

            if (newFolder) {
              // Collect all folders to rename (selected folders and their subfolders)
              const allRenames: { id: string; name: string }[] = [];

              for (const folderToWrap of selectedFolderObjects) {
                const oldFolderPath = folderToWrap.name;
                const wrappedFolderDisplayName = getDisplayName(
                  folderToWrap.name
                );
                const newWrappedFolderPath = `${newFolder.name}/${wrappedFolderDisplayName}`;

                // Find this folder and all its subfolders
                const foldersToRename = shapeFolders?.filter(
                  (f) =>
                    f.name === oldFolderPath ||
                    f.name.startsWith(`${oldFolderPath}/`)
                );

                if (foldersToRename) {
                  for (const folder of foldersToRename) {
                    let newPath: string;
                    if (folder.name === oldFolderPath) {
                      newPath = newWrappedFolderPath;
                    } else {
                      const relativePath = folder.name.slice(
                        oldFolderPath.length
                      );
                      newPath = `${newWrappedFolderPath}${relativePath}`;
                    }
                    allRenames.push({ id: folder.id, name: newPath });
                  }
                }
              }

              if (allRenames.length > 0) {
                await bulkRenameFolders(allRenames);
              }

              logEvent({
                eventName: 'Group Created',
                eventType: EventType.Created,
                resourceName: newFolder.name,
                resourceType: EventResource.ShapeFolder,
                metadata: {
                  details: `${selectedFolderObjects.length} groups wrapped into new group "${newFolder.name}"`,
                },
              });

              // Select the new folder and trigger rename mode
              setSelectedFolder(newFolder.id);
              setRenamingFolderId(newFolder.id);
            }
            return;
          }
        }
      }

      // Case 2: A single folder is selected - wrap that folder
      // This happens when uniqueFolderIds.length === 1 and selectedFolder is set
      if (uniqueFolderIds.length === 1 && selectedFolder) {
        const folderToWrap = shapeFolders?.find((f) => f.id === selectedFolder);
        if (folderToWrap) {
          // Get the parent path of the folder to wrap
          const parentPath = getParentPath(folderToWrap.name);

          // Create new folder at the same level as the folder we're wrapping
          const baseName = t('common:shape-folder');
          const fullName = parentPath ? `${parentPath}/${baseName}` : baseName;
          const newFolderName = findNextAvailableFolderName(fullName);
          const newFolder = await createFolder(newFolderName);

          if (newFolder) {
            const oldFolderPath = folderToWrap.name;
            const wrappedFolderDisplayName = getDisplayName(folderToWrap.name);
            const newWrappedFolderPath = `${newFolder.name}/${wrappedFolderDisplayName}`;

            // Find all subfolders that need to be renamed
            const foldersToRename = shapeFolders?.filter(
              (f) =>
                f.name === oldFolderPath ||
                f.name.startsWith(`${oldFolderPath}/`)
            );

            // Build rename operations for all affected folders
            if (foldersToRename && foldersToRename.length > 0) {
              const renames = foldersToRename.map((folder) => {
                let newPath: string;
                if (folder.name === oldFolderPath) {
                  newPath = newWrappedFolderPath;
                } else {
                  const relativePath = folder.name.slice(oldFolderPath.length);
                  newPath = `${newWrappedFolderPath}${relativePath}`;
                }
                return { id: folder.id, name: newPath };
              });
              await bulkRenameFolders(renames);
            }

            logEvent({
              eventName: 'Group Created',
              eventType: EventType.Created,
              resourceName: newFolder.name,
              resourceType: EventResource.ShapeFolder,
              metadata: {
                details: `Group "${folderToWrap.name}" wrapped into new group "${newFolder.name}"`,
              },
            });

            // Select the new folder and trigger rename mode
            setSelectedFolder(newFolder.id);
            setRenamingFolderId(newFolder.id);
          }
          return;
        }
      }
    }

    // Case 3: Only shapes are selected - create folder and move shapes into it
    if (commonParentFolderId === undefined) {
      // Shapes are from different parent folders - cannot group
      return;
    }

    // Determine the parent path for the new folder
    let parentFolderPath: string | null = null;
    if (commonParentFolderId) {
      const parentFolder = shapeFolders?.find(
        (f) => f.id === commonParentFolderId
      );
      if (parentFolder) {
        parentFolderPath = parentFolder.name;
      }
    }

    // Build the new folder name with parent path prefix
    const baseName = t('common:shape-folder');
    const fullName = parentFolderPath
      ? `${parentFolderPath}/${baseName}`
      : baseName;
    const newFolderName = findNextAvailableFolderName(fullName);
    const newFolder = await createFolder(newFolderName);

    if (newFolder && selectedShapesRef.current) {
      const shapeIds: string[] = [];
      const sheetShapeIds: string[] = [];
      selectedShapesRef.current.forEach((shape) => {
        if (shape.__typename === 'Shape') {
          shapeIds.push(shape.id);
        } else if (shape.__typename === 'SheetShape') {
          sheetShapeIds.push(shape.id);
        }
      });

      await moveShapesToFolder(shapeIds, sheetShapeIds, newFolder.id);

      logEvent({
        eventName: 'Group Created',
        eventType: EventType.Created,
        resourceName: newFolder.name,
        resourceType: EventResource.ShapeFolder,
        metadata: {
          affectedResources: [...shapeIds, ...sheetShapeIds],
          details: `${[...shapeIds, ...sheetShapeIds].length} shape${
            [...shapeIds, ...sheetShapeIds].length === 1 ? '' : 's'
          } added to group ${newFolder.name}`,
        },
      });

      // Select the new folder and trigger rename mode
      setSelectedFolder(newFolder.id);
      setRenamingFolderId(newFolder.id);
    }
  }, [
    bulkRenameFolders,
    commonParentFolderId,
    createFolder,
    findNextAvailableFolderName,
    logEvent,
    moveShapesToFolder,
    selectedFolder,
    setSelectedFolder,
    shapeFolders,
    t,
  ]);

  /**
   * Create an empty group/folder without moving any shapes into it.
   * The new group is created at the appropriate level:
   * - If a folder is selected, create inside that folder
   * - If a single shape is selected inside "Group A", new group is inside "Group A"
   * - If multiple shapes are selected, use the shallowest level among them
   */
  // eslint-disable-next-line complexity
  const createEmptyGroup = useCallback(async () => {
    // Determine the parent folder path
    let parentFolderPath: string | null = null;

    // First, check if a folder is selected (clicking a folder selects it and all its shapes)
    if (selectedFolder) {
      const folder = shapeFolders?.find((f) => f.id === selectedFolder);
      if (folder) {
        parentFolderPath = folder.name;
      }
    } else {
      // Fall back to finding the shallowest folder among selected shapes
      const selectedShapes = selectedShapesRef.current;

      if (selectedShapes && selectedShapes.length > 0) {
        let shallowestDepth = Infinity;

        for (const shape of selectedShapes) {
          const folderId = shape.folder;
          if (!folderId) {
            // Shape has no folder - this is top level (depth 0)
            shallowestDepth = 0;
            parentFolderPath = null;
            break; // Can't get shallower than top level
          }

          // Find the folder to get its full path
          const folder = shapeFolders?.find((f) => f.id === folderId);
          if (folder) {
            const depth = (folder.name.match(/\//g) || []).length + 1;
            if (depth < shallowestDepth) {
              shallowestDepth = depth;
              parentFolderPath = folder.name;
            }
          }
        }
      }
    }

    // Build the new folder name with parent path prefix
    const baseName = t('common:shape-folder');
    const fullName = parentFolderPath
      ? `${parentFolderPath}/${baseName}`
      : baseName;
    const newFolderName = findNextAvailableFolderName(fullName);
    const newFolder = await createFolder(newFolderName);

    if (newFolder) {
      logEvent({
        eventName: 'Group Created',
        eventType: EventType.Created,
        resourceName: newFolder.name,
        resourceType: EventResource.ShapeFolder,
        metadata: {
          details: `Empty group "${newFolder.name}" created`,
        },
      });
      // Select the new folder so user can see it
      setSelectedFolder(newFolder.id);
      // Trigger rename mode for the new folder
      setRenamingFolderId(newFolder.id);
    }
  }, [
    createFolder,
    findNextAvailableFolderName,
    logEvent,
    selectedFolder,
    setSelectedFolder,
    shapeFolders,
    t,
  ]);

  const ungroupShapesFromFolder = useCallback(
    async (node: ShapeNodeType) => {
      const shapeIds: string[] = [];
      const sheetShapeIds: string[] = [];
      const shapes: ShapeNodeType[] = selectedShapesRef.current;
      shapes.push(node);
      for (const shape of shapes) {
        if (shape.__typename === 'Shape') {
          shapeIds.push(shape.id);
        } else if (shape.__typename === 'SheetShape') {
          sheetShapeIds.push(shape.id);
        }
      }
      await removeShapesFromFolder(shapeIds, sheetShapeIds);
    },
    [removeShapesFromFolder]
  );

  /**
   * Ungroup a folder: move all its contents (shapes and subfolders) up one level,
   * then delete the folder.
   */
  const ungroupFolder = useCallback(
    async (folderId: string) => {
      const folder = shapeFolders?.find((f) => f.id === folderId);
      if (!folder) return;

      const folderPath = folder.name;
      const parentPath = getParentPath(folderPath);

      // 1. Move all shapes in this folder to the parent folder (or root)
      const shapesInFolder = shapes.filter((s) => s.folder === folderId);
      if (shapesInFolder.length > 0) {
        const shapeIds = shapesInFolder
          .filter((s) => s.__typename === 'Shape')
          .map((s) => s.id);
        const sheetShapeIds = shapesInFolder
          .filter((s) => s.__typename === 'SheetShape')
          .map((s) => s.id);

        if (parentPath) {
          // Find parent folder by path
          const parentFolder = shapeFolders?.find((f) => f.name === parentPath);
          if (parentFolder) {
            await moveShapesToFolder(shapeIds, sheetShapeIds, parentFolder.id);
          }
        } else {
          // Move to root (remove from folder)
          await removeShapesFromFolder(shapeIds, sheetShapeIds);
        }
      }

      // 2. Move all subfolders up one level by renaming them
      const subfolders = shapeFolders?.filter(
        (f) => f.name.startsWith(`${folderPath}/`) && f.id !== folderId
      );
      if (subfolders && subfolders.length > 0) {
        const renames = subfolders.map((subfolder) => {
          // Remove the folder path prefix and add parent path
          const relativePath = subfolder.name.slice(folderPath.length + 1); // +1 for the "/"
          const newPath = parentPath
            ? `${parentPath}/${relativePath}`
            : relativePath;
          return { id: subfolder.id, name: newPath };
        });
        await bulkRenameFolders(renames);
      }

      // 3. Delete the folder
      await deleteFolder(folderId);

      logEvent({
        eventName: 'Group Ungrouped',
        eventType: EventType.Modified,
        resourceName: folder.name,
        resourceType: EventResource.ShapeFolder,
        metadata: {
          details: `Group "${folder.name}" ungrouped, contents moved to ${
            parentPath ?? 'root'
          }`,
        },
      });
    },
    [
      bulkRenameFolders,
      deleteFolder,
      logEvent,
      moveShapesToFolder,
      removeShapesFromFolder,
      shapeFolders,
      shapes,
    ]
  );

  // Track expanded state by path (for hierarchical folders)
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  // Track folder that should auto-start in rename mode (for newly created folders)
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);

  const {
    isOpen: showDeleteSelectedShapesModal,
    onOpen: onDeleteSelectedShapesModalOpen,
    onClose: onDeleteSelectedShapesModalClose,
  } = useDisclosure();

  // Build folder hierarchy from flat folders and autoshapes
  const folderHierarchy = useMemo(() => {
    return buildFolderHierarchy(shapeFolders ?? [], autoShapes ?? []);
  }, [shapeFolders, autoShapes]);

  // Keep the original sortedFolders for backward compatibility with some functions
  const sortedFolders = useMemo(() => {
    // Perhaps we should add Autshapes as folders?
    let expandableNodes: (ShapeFolderDeepFragment | AutoShapeDeepFragment)[] =
      [];
    if (shapeFolders) {
      expandableNodes.push(...shapeFolders);
    }
    if (autoShapes) {
      expandableNodes.push(...autoShapes);
    }

    const sortedFolders = expandableNodes?.sort((a, b) =>
      compareShapeNameWithNumber(a.name, b.name)
    );
    return sortedFolders || [];
  }, [autoShapes, shapeFolders]);

  const sortedShapes = useMemo(() => {
    const sorted = shapes.sort((a, b) =>
      compareShapeNameWithNumber(a.name, b.name)
    );
    return sorted || [];
  }, [shapes]);

  const shapesIndexedByFolder = useMemo(() => {
    return new Map<string, Array<ShapeDeepFragment | SheetShapeDeepFragment>>(
      sortedFolders.map((folder) => [
        folder.id,
        shapes
          .filter(
            (shape) =>
              shape.folder === folder.id || shape.autoShapeId === folder.id
          )
          .sort((a, b) => compareShapeNameWithNumber(a.name, b.name)),
      ])
    );
  }, [sortedFolders, shapes]);

  const firstShapeWithoutFolder = useRef<VirtualItem | null>(null);

  // Flatten the hierarchy for virtualization, including level information
  const flattenedHierarchy = useMemo(() => {
    const result = flattenHierarchyForVirtualization(
      folderHierarchy,
      expandedPaths,
      shapesIndexedByFolder,
      autoShapes ?? []
    );
    return result;
  }, [folderHierarchy, expandedPaths, shapesIndexedByFolder, autoShapes]);

  // Add a structure that gets all the items in order
  const itemsToVirtualize = useMemo(() => {
    // If no hierarchy (no folders), just return sorted shapes
    if (folderHierarchy.length === 0) {
      return sortedShapes.map((shape) => ({
        id: shape.id,
        node: shape as
          | ShapeDeepFragment
          | SheetShapeDeepFragment
          | ShapeFolderDeepFragment
          | AutoShapeDeepFragment
          | VirtualFolderNode,
        level: 0,
        displayName: shape.name,
        isVirtual: false,
      }));
    }

    // Map flattened hierarchy items to the format expected by virtualization
    const items: Array<{
      id: string;
      node:
        | ShapeDeepFragment
        | SheetShapeDeepFragment
        | ShapeFolderDeepFragment
        | AutoShapeDeepFragment
        | VirtualFolderNode;
      level: number;
      displayName: string;
      isVirtual: boolean;
      hasChildren?: boolean;
    }> = [];

    for (const flatItem of flattenedHierarchy) {
      if (flatItem.type === 'folder') {
        items.push({
          id: flatItem.item.id,
          node: flatItem.item.node,
          level: flatItem.item.level,
          displayName: flatItem.item.displayName,
          isVirtual: flatItem.item.isVirtual,
          hasChildren: flatItem.item.hasChildren,
        });
      } else {
        items.push({
          id: flatItem.item.id,
          node: flatItem.item.node,
          level: flatItem.item.level,
          displayName: flatItem.item.node.name,
          isVirtual: false,
        });
      }
    }

    // Add shapes without folder at the end
    for (const shape of shapesWithNoFolder) {
      items.push({
        id: shape.id,
        node: shape,
        level: 0,
        displayName: shape.name,
        isVirtual: false,
      });
    }

    return items;
  }, [folderHierarchy, flattenedHierarchy, sortedShapes, shapesWithNoFolder]);

  const persistedVirtualFoldersRef = useRef(
    new Map<VirtualItem['key'], VirtualItem>()
  );

  const [virtualItems, setVirtualItems] = useState<VirtualItem[]>([]);
  const shapeVirtualizer = useVirtualizer({
    count: itemsToVirtualize.length,
    estimateSize: () => SHAPE_ITEM_SIZE,
    getItemKey: (index) => `${itemsToVirtualize[index].id}`,
    getScrollElement: () => panelRef.current,
    overscan: 30,
    onChange: (instance) => {
      setVirtualItems(instance.getVirtualItems());
    },
  });

  // since we are setting virtual items onchange above, which mostly means onscroll,
  // we have to manually listen to the other events where we might be interested in resizing and handle that manually below
  // the alternative is calling `getVirtualItems` on every render like we were doing before
  useLayoutEffect(() => {
    shapeVirtualizer.measure();
    setVirtualItems(shapeVirtualizer.getVirtualItems());
  }, [
    expandedPaths,
    shapeFolders,
    shapes,
    itemsToVirtualize,
    shapeVirtualizer,
  ]);

  // Get current virtual items - use state if available, otherwise get fresh from virtualizer
  // This ensures we have valid items even on initial render before useLayoutEffect runs
  const currentVirtualItems = useMemo(() => {
    // Check if virtualItems state is in sync with itemsToVirtualize
    // If not, get fresh items from the virtualizer
    if (virtualItems.length === 0 && itemsToVirtualize.length > 0) {
      return shapeVirtualizer.getVirtualItems();
    }
    // Also check if keys are in sync
    const firstVirtualKey = virtualItems[0]?.key;
    const firstItemId = itemsToVirtualize[0]?.id;
    if (firstVirtualKey && firstItemId && firstVirtualKey !== firstItemId) {
      // Keys are out of sync, get fresh items
      return shapeVirtualizer.getVirtualItems();
    }
    return virtualItems;
  }, [virtualItems, itemsToVirtualize, shapeVirtualizer]);

  // Create a map from item ID to itemsToVirtualize entry for quick lookup
  const itemsToVirtualizeMap = useMemo(() => {
    const map = new Map<string, typeof itemsToVirtualize[number]>();
    for (const item of itemsToVirtualize) {
      map.set(item.id, item);
    }
    return map;
  }, [itemsToVirtualize]);

  const allShapesSorted = useMemo(() => {
    const allItems: {
      type: string;
      folder?:
        | ShapeFolderDeepFragment
        | AutoShapeDeepFragment
        | VirtualFolderNode;
      shape?: ShapeDeepFragment | SheetShapeDeepFragment;
      folderId?: string;
      folderPath?: string;
    }[] = [];

    // Use the flattened hierarchy to build the list
    for (const flatItem of flattenedHierarchy) {
      if (flatItem.type === 'folder') {
        allItems.push({
          type: 'folder',
          folder: flatItem.item.node,
          folderPath: flatItem.item.fullPath,
        });
      } else {
        allItems.push({
          type: 'shape',
          shape: flatItem.item.node,
          folderPath: flatItem.item.parentPath ?? undefined,
        });
      }
    }

    for (const shape of shapesWithNoFolder) {
      allItems.push({ type: 'shape', shape, folderId: undefined });
    }

    return allItems; // Return all folders and shapes in the correct order
  }, [flattenedHierarchy, shapesWithNoFolder]);

  const allShapesSortedRef = useRef(allShapesSorted);

  useEffect(() => {
    allShapesSortedRef.current = allShapesSorted;
  }, [allShapesSorted]);

  const metaKeyPressRef = useRef(false);
  const shiftKeyPressRef = useRef(false);
  const lastSelectedShapeIdRef = useRef<string | null>(null);

  // Toggle folder expansion by path (for hierarchical folders)
  const toggleFolderExpansion = useCallback(
    (folderIdOrPath: string) => {
      // Find the path for this folder
      // If it starts with "virtual:", it's already a path-based ID
      // Otherwise, look up the folder's name (which is its path)
      let path = folderIdOrPath;

      // If it's not a virtual folder ID, look up the folder's name
      if (!folderIdOrPath.startsWith('virtual:')) {
        const folder = sortedFolders.find((f) => f.id === folderIdOrPath);
        if (folder) {
          path = folder.name;
        }
      } else {
        // Extract path from virtual folder ID
        path = folderIdOrPath.replace('virtual:', '');
      }

      setExpandedPaths((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(path)) {
          newSet.delete(path);
        } else {
          newSet.add(path);
        }
        return newSet;
      });
    },
    [sortedFolders]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        metaKeyPressRef.current = true;
      } else if (e.shiftKey) {
        shiftKeyPressRef.current = true;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
        metaKeyPressRef.current = false;
        shiftKeyPressRef.current = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleShapeSelection = useCallback(
    (
      shape: ShapeDeepFragment | SheetShapeDeepFragment,
      newSelection: DbIdsPerModel
    ) => {
      if (!newSelection[shape.urn]) {
        newSelection[shape.urn] = [];
      }

      // If the shape is already selected, deselect it
      if (newSelection[shape.urn].includes(shape.dbId)) {
        deselectDbIds({ [shape.urn]: [shape.dbId] });
        newSelection[shape.urn] = newSelection[shape.urn].filter(
          (id) => id !== shape.dbId
        );

        if (newSelection[shape.urn].length === 0) {
          delete newSelection[shape.urn];
        }
      } else {
        newSelection[shape.urn].push(shape.dbId);
      }
    },
    [deselectDbIds]
  );

  // Helper to collect all shapes recursively from a hierarchy node
  const collectShapesRecursively = useCallback(
    (folderId: string): Array<ShapeDeepFragment | SheetShapeDeepFragment> => {
      // First, try to find the node in the hierarchy by ID or path
      let hierarchyNode: HierarchyNode | undefined;

      // Check if this is a virtual folder ID
      if (folderId.startsWith('virtual:')) {
        const path = folderId.replace('virtual:', '');
        hierarchyNode = findNodeByPath(folderHierarchy, path);
      } else {
        // Find the folder/autoshape and get its path
        const folder = sortedFolders.find((f) => f.id === folderId);
        if (folder) {
          hierarchyNode = findNodeByPath(folderHierarchy, folder.name);
        }
      }

      if (hierarchyNode) {
        return collectAllShapesInHierarchy(
          hierarchyNode,
          shapesIndexedByFolder
        );
      }

      // Fallback to the old behavior if hierarchy lookup fails
      return shapesRef.current.filter(
        (s) => s.folder === folderId || s.autoShapeId === folderId
      );
    },
    [folderHierarchy, sortedFolders, shapesIndexedByFolder]
  );

  const handleFolderSelection = useCallback(
    // eslint-disable-next-line complexity
    (folderId: string, newSelection: DbIdsPerModel) => {
      // Collect all shapes recursively (including nested folders)
      const shapesInFolder = collectShapesRecursively(folderId);

      const allShapesSelected = shapesInFolder.every((shape) => {
        return (
          newSelection[shape.urn] &&
          newSelection[shape.urn].includes(shape.dbId)
        );
      });

      if (metaKeyPressRef.current) {
        if (allShapesSelected) {
          // Collect all the shapes to be deselected
          const shapesToDeselect: Record<string, number[]> = {};

          for (const shape of shapesInFolder) {
            if (newSelection[shape.urn]) {
              if (!shapesToDeselect[shape.urn]) {
                shapesToDeselect[shape.urn] = [];
              }
              shapesToDeselect[shape.urn].push(shape.dbId);

              newSelection[shape.urn] = newSelection[shape.urn].filter(
                (id) => id !== shape.dbId
              );

              if (newSelection[shape.urn].length === 0) {
                delete newSelection[shape.urn]; // Remove the key if no shapes are selected
              }
            }
          }

          // Batch deselect all shapes within the folder at once
          deselectDbIds(shapesToDeselect);
          setSelectedFolder(null); // Deselect the folder itself
        } else {
          // Select all shapes within the folder but NOT the folder itself
          for (const shape of shapesInFolder) {
            if (!newSelection[shape.urn]) {
              newSelection[shape.urn] = [];
            }
            if (!newSelection[shape.urn].includes(shape.dbId)) {
              newSelection[shape.urn].push(shape.dbId);
            }
          }
        }
      } else {
        // If meta is not pressed, we treat this as a regular folder selection
        // Virtual folders don't have a real folder ID, so don't set selectedFolder for them
        // AutoShapes also shouldn't set selectedFolder (can't drop shapes into them)
        if (folderId.startsWith('virtual:')) {
          setSelectedFolder(null);
        } else if (autoShapes?.some((a) => a.id === folderId)) {
          // This folder is an AutoShape - don't set selectedFolder
          setSelectedFolder(null);
        } else {
          setSelectedFolder(folderId);
        }
        for (const shape of shapesInFolder) {
          if (!newSelection[shape.urn]) {
            newSelection[shape.urn] = [];
          }
          if (!newSelection[shape.urn].includes(shape.dbId)) {
            newSelection[shape.urn].push(shape.dbId);
          }
        }
      }
    },
    [setSelectedFolder, deselectDbIds, collectShapesRecursively, autoShapes]
  );

  const handleNodeSelect = useCallback(
    // eslint-disable-next-line complexity
    (
      node:
        | ShapeDeepFragment
        | SheetShapeDeepFragment
        | ShapeFolderDeepFragment
        | AutoShapeDeepFragment
        | VirtualFolderNode
    ) => {
      const isShape =
        node.__typename === 'Shape' || node.__typename === 'SheetShape';
      const isFolder = !isShape; // If it's not a shape, it's a folder (or virtual folder)

      let newSelection = { ...selectedDbIdsInternalRef.current };

      // Handle Shift-Select (do not select the folder itself)
      if (shiftKeyPressRef.current && lastSelectedShapeIdRef.current) {
        const allItemsSorted = allShapesSortedRef.current;

        // Find indices of the last selected item and the currently clicked item
        const startIdx = allItemsSorted.findIndex(
          (item) =>
            (item.type === 'shape' &&
              item.shape?.id === lastSelectedShapeIdRef.current) ||
            (item.type === 'folder' &&
              item.folder?.id === lastSelectedShapeIdRef.current)
        );
        const endIdx = allItemsSorted.findIndex(
          (item) =>
            (item.type === 'shape' && item.shape?.id === node.id) ||
            (item.type === 'folder' && item.folder?.id === node.id)
        );

        if (startIdx !== -1 && endIdx !== -1) {
          const [minIdx, maxIdx] = [
            Math.min(startIdx, endIdx),
            Math.max(startIdx, endIdx),
          ];
          const itemsInRange = allItemsSorted.slice(minIdx, maxIdx + 1);

          for (const item of itemsInRange) {
            if (item.type === 'folder' && item.folder) {
              handleFolderSelection(item.folder.id, newSelection);
            } else if (item.type === 'shape' && item.shape) {
              if (!newSelection[item.shape.urn]) {
                newSelection[item.shape.urn] = [];
              }
              if (!newSelection[item.shape.urn].includes(item.shape.dbId)) {
                newSelection[item.shape.urn].push(item.shape.dbId);
              }
            }
          }
        }
      } else {
        // Handle regular click (no meta or shift key)
        if (!metaKeyPressRef.current) {
          newSelection = {};
          //setSelectedDbIds({});
          setSelectedFolder(null);
        }

        // For regular clicks, select the folder or shape
        if (isShape) {
          handleShapeSelection(node, newSelection);
          lastSelectedShapeIdRef.current = node.id; // Store the last clicked shape for shift-select
        } else if (isFolder) {
          handleFolderSelection(node.id, newSelection); // Select the folder and its shapes, or deselect with meta key
          lastSelectedShapeIdRef.current = node.id; // Store the last clicked folder for shift-select
        }
      }

      // Update the selection state
      setSelectedDbIds(newSelection);
    },
    [
      setSelectedDbIds,
      handleFolderSelection,
      setSelectedFolder,
      handleShapeSelection,
    ]
  );

  const handleShapeMove = useCallback(
    async (
      shapesToMove: (ShapeDeepFragment | SheetShapeDeepFragment)[],
      folder: ShapeFolderDeepFragment | null
    ) => {
      // Check if the shape is already in the folder
      const isShapeInFolder = (
        shape: ShapeDeepFragment | SheetShapeDeepFragment
      ) => shape.folder === folder?.id;

      if (shapesToMove.every(isShapeInFolder)) {
        return; // Do nothing if all shapes are already in the folder
      }

      const [shapeIdsToMove, sheetShapeIdsToMove] = shapesToMove.reduce(
        (acc, shape) => {
          if (shape.__typename === 'Shape') {
            acc[0].push(shape.id);
          } else {
            acc[1].push(shape.id);
          }
          return acc;
        },
        [[], []] as [string[], string[]]
      );

      if (folder) {
        await moveShapesToFolder(
          shapeIdsToMove,
          sheetShapeIdsToMove,
          folder.id
        );
        lastSelectedShapeIdRef.current = null;

        logEvent({
          eventName: 'Shapes Moved to Group',
          eventType: EventType.Modified,
          resourceName: folder.name,
          resourceType: EventResource.Shape,
          metadata: {
            affectedResources: [...shapeIdsToMove, ...sheetShapeIdsToMove],
            details: `${shapesToMove.length} shape${
              shapesToMove.length === 1 ? '' : 's'
            } moved to group ${folder.name}`,
          },
        });
      } else {
        await removeShapesFromFolder(shapeIdsToMove, sheetShapeIdsToMove);

        logEvent({
          eventName: 'Shapes Removed from Group',
          eventType: EventType.Modified,
          resourceName: 'Shapes',
          resourceType: EventResource.Shape,
          metadata: {
            affectedResources: [...shapeIdsToMove, ...sheetShapeIdsToMove],
            details: `${shapesToMove.length} shape${
              shapesToMove.length === 1 ? '' : 's'
            } removed from group`,
          },
        });
      }
    },
    [moveShapesToFolder, removeShapesFromFolder, logEvent]
  );

  /**
   * Handle moving a folder into another folder by renaming it with the new path.
   */
  const handleMoveFolder = useCallback(
    async (folderId: string, newPath: string) => {
      await renameFolder(folderId, newPath);

      logEvent({
        eventName: 'Group Moved',
        eventType: EventType.Modified,
        resourceName: newPath,
        resourceType: EventResource.Shape,
        metadata: {
          affectedResources: [folderId],
          details: `Group moved to ${newPath}`,
        },
      });
    },
    [renameFolder, logEvent]
  );

  /**
   * Handle moving an autoshape into another folder by renaming it with the new path.
   */
  const handleMoveAutoShape = useCallback(
    async (autoShapeId: string, newPath: string) => {
      await renameAutoShape(autoShapeId, newPath);

      logEvent({
        eventName: 'Auto Shape Moved',
        eventType: EventType.Modified,
        resourceName: newPath,
        resourceType: EventResource.AutoShape,
        metadata: {
          affectedResources: [autoShapeId],
          details: `Auto shape moved to ${newPath}`,
        },
      });
    },
    [renameAutoShape, logEvent]
  );

  const handleDragOverOutside = (e: React.DragEvent) => {
    e.preventDefault();
    setIsHoveredOutside(true);
  };

  const handleDragLeaveOutside = () => {
    setIsHoveredOutside(false);
  };

  const handleDropOutside = (e: React.DragEvent) => {
    e.preventDefault();
    setIsHoveredOutside(false);

    const rawData = e.dataTransfer.getData('text/plain');
    if (!rawData) return;

    const dragData = JSON.parse(rawData) as DragData;

    // Handle folder being dropped outside (move to root level)
    if (dragData.type === 'folder') {
      const displayName = getDisplayName(dragData.folderPath);
      // Only move if not already at root level
      if (dragData.folderPath !== displayName) {
        handleMoveFolder(dragData.folder.id, displayName);
      }
      return;
    }

    // Handle autoshape being dropped outside (move to root level)
    if (dragData.type === 'autoshape') {
      const displayName = getDisplayName(dragData.autoShapePath);
      // Only move if not already at root level
      if (dragData.autoShapePath !== displayName) {
        handleMoveAutoShape(dragData.autoShape.id, displayName);
      }
      return;
    }

    // Handle shapes being dropped outside (existing behavior)
    const shapesToMove =
      dragData.type === 'shapes'
        ? dragData.shapes
        : (dragData as unknown as (
            | ShapeDeepFragment
            | SheetShapeDeepFragment
          )[]);

    if (shapesToMove && shapesToMove.length > 0) {
      handleShapeMove(shapesToMove, null);
    }
  };

  const handleRename = useCallback(
    // eslint-disable-next-line complexity
    async (node: ShapeNodeType, newName: string) => {
      // Check if the node is part of a selection and if all selected items are Shape or SheetShape
      const isShapePartOfSelection = selectedShapesRef.current.find(
        (ss) => ss.id === node.id
      );
      const isValidSelection = selectedShapesRef.current.every(
        (shape) =>
          shape.__typename === 'Shape' || shape.__typename === 'SheetShape'
      );

      if (
        (node.__typename === 'Shape' || node.__typename === 'SheetShape') &&
        isShapePartOfSelection &&
        isValidSelection
      ) {
        // Split shapes by type
        const shapes3D = selectedShapesRef.current.filter(
          (shape): shape is ShapeDeepFragment => shape.__typename === 'Shape'
        );
        const sheetShapes = selectedShapesRef.current.filter(
          (shape): shape is SheetShapeDeepFragment =>
            shape.__typename === 'SheetShape'
        );

        try {
          // Execute all mutations in parallel
          await Promise.all([
            ...shapes3D.map((shape) =>
              updateShapeMutation({
                input: {
                  id: shape.id,
                  patch: { name: newName },
                },
              })
            ),
            ...sheetShapes.map((shape) =>
              updateSheetShapeMutation({
                input: {
                  id: shape.id,
                  patch: { name: newName },
                },
              })
            ),
          ]);
        } catch (error) {
          console.error(error);
          toast({
            title: t('project:shapes.error.rename.title'),
            status: 'error',
          });
        } finally {
          logEvent({
            eventName: 'Shapes Renamed',
            eventType: EventType.Modified,
            resourceName: newName,
            resourceType: EventResource.Shape,
            metadata: {
              affectedResources: [
                ...selectedShapesRef.current.map((shape) => shape.id),
              ],
              oldName:
                selectedShapesRef.current.length > 1
                  ? undefined
                  : selectedShapesRef.current[0].name,
              newName,
              details: `${selectedShapesRef.current.length} shapes renamed to ${newName}`,
            },
          });
        }
      } else {
        // Handle single item rename as before
        if (node.__typename === 'Shape') {
          const { error } = await updateShapeMutation({
            input: {
              id: node.id,
              patch: { name: newName },
            },
          });
          if (error) {
            console.error(error);
            toast({
              title: t('project:shapes.error.rename.title'),
              status: 'error',
            });
          } else {
            logEvent({
              eventName: 'Shape Renamed',
              eventType: EventType.Modified,
              resourceName: node.name,
              resourceType: EventResource.Shape,
              metadata: {
                affectedResources: [node.id],
                oldName: node.name,
                newName,
              },
            });
          }
        } else if (node.__typename === 'SheetShape') {
          const { error } = await updateSheetShapeMutation({
            input: {
              id: node.id,
              patch: { name: newName },
            },
          });
          if (error) {
            console.error(error);
            toast({
              title: t('project:shapes.error.rename.title'),
              status: 'error',
            });
          } else {
            logEvent({
              eventName: 'Shape Renamed',
              eventType: EventType.Modified,
              resourceName: node.name,
              resourceType: EventResource.SheetShape,
              metadata: {
                oldName: node.name,
                newName: newName,
                affectedResources: [node.id],
              },
            });
          }
        } else if (node.__typename === 'AutoShape') {
          await renameAutoShape(node.id, newName);
          logEvent({
            eventName: 'Auto Shape Renamed',
            eventType: EventType.Modified,
            resourceName: node.name,
            resourceType: EventResource.ShapeFolder,
            metadata: {
              affectedResources: [node.id],
              oldName: node.name,
              newName,
            },
          });
        } else {
          await renameFolder(node.id, newName);
          logEvent({
            eventName: 'Group Renamed',
            eventType: EventType.Modified,
            resourceName: node.name,
            resourceType: EventResource.ShapeFolder,
            metadata: {
              affectedResources: [node.id],
              oldName: node.name,
              newName,
            },
          });
        }
      }
    },
    [
      logEvent,
      renameAutoShape,
      renameFolder,
      t,
      toast,
      updateShapeMutation,
      updateSheetShapeMutation,
    ]
  );

  const handleShow = useCallback(
    (node: ShapeNodeType) => {
      if (node.__typename === 'Shape' || node.__typename === 'SheetShape') {
        const isShapePartOfSelection = selectedShapesRef.current.find(
          (ss) => ss.id === node.id
        );
        let dbidsPerModelToShow = { [node.urn]: [node.dbId] };
        if (isShapePartOfSelection) {
          const dbIdsPerModel: DbIdsPerModel = {};
          for (const shape of selectedShapesRef.current) {
            if (!dbIdsPerModel[shape.urn]) {
              dbIdsPerModel[shape.urn] = [];
            }
            dbIdsPerModel[shape.urn].push(shape.dbId);
          }
          dbidsPerModelToShow = dbIdsPerModel;
        }
        show(dbidsPerModelToShow);
      } else {
        const shapesInFolder = shapesRef.current.filter(
          (s) => s.folder === node.id || s.autoShapeId === node.id
        );
        const dbIdsPerModel: DbIdsPerModel = {};
        for (const shape of shapesInFolder) {
          if (!dbIdsPerModel[shape.urn]) {
            dbIdsPerModel[shape.urn] = [];
          }
          dbIdsPerModel[shape.urn].push(shape.dbId);
        }
        show(dbIdsPerModel);
      }
    },
    [show]
  );

  const handleHide = useCallback(
    (node: ShapeNodeType) => {
      if (node.__typename === 'Shape' || node.__typename === 'SheetShape') {
        const isShapePartOfSelection = selectedShapesRef.current.find(
          (ss) => ss.id === node.id
        );
        let dbidsPerModelToHide = { [node.urn]: [node.dbId] };
        if (isShapePartOfSelection) {
          const dbIdsPerModel: DbIdsPerModel = {};
          for (const shape of selectedShapesRef.current) {
            if (!dbIdsPerModel[shape.urn]) {
              dbIdsPerModel[shape.urn] = [];
            }
            dbIdsPerModel[shape.urn].push(shape.dbId);
          }
          dbidsPerModelToHide = dbIdsPerModel;
        }
        hide(dbidsPerModelToHide);
      } else {
        const shapesInFolder = shapesRef.current.filter(
          (s) => s.folder === node.id || s.autoShapeId === node.id
        );
        const dbIdsPerModel: DbIdsPerModel = {};
        for (const shape of shapesInFolder) {
          if (!dbIdsPerModel[shape.urn]) {
            dbIdsPerModel[shape.urn] = [];
          }
          dbIdsPerModel[shape.urn].push(shape.dbId);
        }
        hide(dbIdsPerModel);
      }
    },
    [hide]
  );

  const [nodesToDelete, setNodesToDelete] = useState<ShapeNodeType[]>([]);

  // Helper function to group nodes by type
  const groupNodesByType = useCallback((nodesToDelete: ShapeNodeType[]) => {
    const shapes: ShapeNodeType[] = [];
    const sheetShapes: ShapeNodeType[] = [];
    const autoShapes: ShapeNodeType[] = [];
    const folders: ShapeNodeType[] = [];

    for (const node of nodesToDelete) {
      if (node.__typename === 'Shape') {
        shapes.push(node);
      } else if (node.__typename === 'SheetShape') {
        sheetShapes.push(node);
      } else if (node.__typename === 'AutoShape') {
        autoShapes.push(node);
      } else {
        folders.push(node);
      }
    }

    return { shapes, sheetShapes, autoShapes, folders };
  }, []);

  // Helper function to delete shapes in a folder
  const deleteShapesInFolder = useCallback(
    async (folderId: string) => {
      const shapesInFolder = shapesRef.current.filter(
        (s) => s.folder === folderId
      );
      const BIMShapes = shapesInFolder.filter((s) => s.__typename === 'Shape');
      const sheetShapesInFolder = shapesInFolder.filter(
        (s) => s.__typename === 'SheetShape'
      );

      // Cleanup Protan roof flags for drain line shapes inside the folder before deletion
      await cleanupRoofFlagsForDeletedDrainLines(
        sheetShapesInFolder as (ShapeDeepFragment | SheetShapeDeepFragment)[],
        sparkelPropsData,
        projectId,
        isProtan
      );

      // Bulk delete all shapes in the folder
      if (BIMShapes.length > 0) {
        await bulkDeleteShapesMutation({
          input: { shapeIds: BIMShapes.map((s) => s.id) },
        });
      }

      if (sheetShapesInFolder.length > 0) {
        await bulkDeleteSheetShapesMutation({
          input: { sheetShapeIds: sheetShapesInFolder.map((s) => s.id) },
        });
      }
    },
    [
      bulkDeleteShapesMutation,
      bulkDeleteSheetShapesMutation,
      cleanupRoofFlagsForDeletedDrainLines,
      sparkelPropsData,
      projectId,
      isProtan,
    ]
  );

  // Helper function to log deletion events
  const logDeletionEvents = useCallback(
    (
      shapes: ShapeNodeType[],
      autoShapes: ShapeNodeType[],
      folders: ShapeNodeType[]
    ) => {
      if (shapes.length > 0) {
        logEvent({
          eventName: 'Shapes Deleted',
          eventType: EventType.Deleted,
          resourceName: 'Shapes',
          resourceType: EventResource.Shape,
          metadata: {
            details: `${shapes.length} shapes deleted`,
            affectedResources: shapes.map((node) => node.id),
          },
        });
      }

      if (autoShapes.length > 0) {
        logEvent({
          eventName: 'Auto Shapes Deleted',
          eventType: EventType.Deleted,
          resourceName: 'Auto Shapes',
          resourceType: EventResource.Shape,
          metadata: {
            details: `${autoShapes.length} auto shapes deleted`,
            affectedResources: autoShapes.map((node) => node.id),
          },
        });
      }

      if (folders.length > 0) {
        logEvent({
          eventName: 'Shape Folders Deleted',
          eventType: EventType.Deleted,
          resourceName: 'Shape Folders',
          resourceType: EventResource.ShapeFolder,
          metadata: {
            details: `${folders.length} folders deleted`,
            affectedResources: folders.map((node) => node.id),
          },
        });
      }
    },
    [logEvent]
  );

  const handleDelete = useCallback(
    async (nodesToDelete: ShapeNodeType[]) => {
      setSelectedDbIds({});

      const { shapes, sheetShapes, autoShapes, folders } =
        groupNodesByType(nodesToDelete);

      // If Protan: analyze drain line sheet shapes that will be deleted to clean up roof flags
      await cleanupRoofFlagsForDeletedDrainLines(
        sheetShapes,
        sparkelPropsData,
        projectId,
        isProtan
      );

      // Bulk delete shapes
      if (shapes.length > 0) {
        await bulkDeleteShapesMutation({
          input: { shapeIds: shapes.map((node) => node.id) },
        });
      }

      if (sheetShapes.length > 0) {
        await bulkDeleteSheetShapesMutation({
          input: { sheetShapeIds: sheetShapes.map((node) => node.id) },
        });
      }

      // Delete auto shapes (still individual since they have different cleanup logic)
      for (const node of autoShapes) {
        await deleteAutoShape(node.id);
      }

      // Handle folders - delete all shapes in the folder first, then the folder
      for (const folder of folders) {
        await deleteShapesInFolder(folder.id);
        await deleteFolder(folder.id);
      }

      // Log events for different types
      logDeletionEvents(shapes, autoShapes, folders);
    },
    [
      deleteAutoShape,
      deleteFolder,
      bulkDeleteShapesMutation,
      bulkDeleteSheetShapesMutation,
      setSelectedDbIds,
      groupNodesByType,
      deleteShapesInFolder,
      logDeletionEvents,
      cleanupRoofFlagsForDeletedDrainLines,
      sparkelPropsData,
      projectId,
      isProtan,
    ]
  );

  const onDeleteConfirm = useCallback(async () => {
    await handleDelete(nodesToDelete);
    setNodesToDelete([]);
    onDeleteSelectedShapesModalClose();
    logEvent({
      projectId,
      eventName: 'Shapes Deleted',
      eventType: EventType.Deleted,
      resourceName: 'Shapes',
      resourceType: EventResource.Shape,
      resolved: false,
      metadata: {
        details: `${nodesToDelete.length} shapes deleted`,
        affectedResources: nodesToDelete.map((node) => node.id),
      },
    });
  }, [
    handleDelete,
    logEvent,
    nodesToDelete,
    onDeleteSelectedShapesModalClose,
    projectId,
  ]);

  // Context menu
  const {
    registerItem: registerSheetItem,
    unregisterItem: unregisterSheetItem,
  } = useSheetContextMenuContext();
  const {
    registerItem: registerSheetViewerV2Item,
    unregisterItem: unregisterSheetViewerV2Item,
  } = useSheetViewerV2ContextMenuContext();
  const { registerItem: registerBIMItem, unregisterItem: unregisterBIMItem } =
    useForgeContextMenuContext();

  const registerItem =
    viewerMode === ViewerMode.Models
      ? registerBIMItem
      : viewerMode === ViewerMode.Sheets
      ? registerSheetItem
      : registerSheetViewerV2Item;

  const unregisterItem =
    viewerMode === ViewerMode.Models
      ? unregisterBIMItem
      : viewerMode === ViewerMode.Sheets
      ? unregisterSheetItem
      : unregisterSheetViewerV2Item;

  const handleEdit = useCallback(() => {
    if (selectedShapes.length === 0) {
      return;
    }
    const shape = selectedShapes[0];
    if (viewerMode === ViewerMode.Models) {
      edit3dShape(shape.id);
    } else {
      editSheetShape(shape.id);
    }
  }, [selectedShapes, viewerMode, edit3dShape, editSheetShape]);

  useEffect(() => {
    if (readOnly || !selectedShapes.length) {
      return;
    }

    const shape = selectedShapes[0];

    registerItem({
      id: `rename-shape-${shape.id}`,
      label: t('project:shapes.rename-shape'),
      group: 'shapes',
      icon: <SparkelIcon size="sm" icon={faPen} fixedWidth />,
      hidden: (params) => {
        // Show when there is any selection; hide only if nothing is selected
        return !params.props?.hasSelectedElements;
      },
      onClick: ({
        triggerEvent,
      }: {
        triggerEvent: React.MouseEvent | React.TouchEvent | React.KeyboardEvent;
      }) => {
        let x = 0;
        let y = 0;
        if (
          triggerEvent &&
          'clientX' in triggerEvent &&
          'clientY' in triggerEvent
        ) {
          const evt = triggerEvent as React.MouseEvent;
          x = evt.clientX;
          y = evt.clientY;
        }
        openRenamePopover(shape, { x, y });
      },
    });

    registerItem({
      id: `edit-shape-${shape.id}`,
      label: t('project:shapes.edit-shape'),
      group: 'shapes',
      icon: <SparkelIcon size="sm" icon={faEdit} fixedWidth />,
      hidden: (params) => {
        if (typeof params.props?.target === 'undefined') {
          return true;
        }

        return !(
          params.props.target.dbId === shape.dbId &&
          params.props.target.modelUrn === shape.urn
        );
      },
      onClick: handleEdit,
    });

    registerItem({
      id: 'delete-selected-shapes',
      label: t('project:shapes.delete'),
      group: 'shapes',
      icon: (
        <SparkelIcon size="sm" icon={faTrash} color={'red.400'} fixedWidth />
      ),
      hidden: (params) => {
        if (!params.props?.hasSelectedElements) {
          return true;
        }

        return false;
      },
      onClick: () => {
        setNodesToDelete(selectedShapes);
        onDeleteSelectedShapesModalOpen();
      },
    });
    return () => {
      unregisterItem(`rename-shape-${shape.id}`);
      unregisterItem(`edit-shape-${shape.id}`);
      unregisterItem('delete-selected-shapes');
    };
  }, [
    edit3dShape,
    editSheetShape,
    handleEdit,
    openRenamePopover,
    onDeleteSelectedShapesModalOpen,
    readOnly,
    registerItem,
    selectedShapes,
    t,
    unregisterItem,
    viewerMode,
  ]);

  const onDeleteHandler = useCallback((node: ShapeNodeType) => {
    const isNodeSeleceted = selectedShapesRef.current.find(
      (ss) => ss.id === node.id
    );
    const nodes = isNodeSeleceted ? selectedShapesRef.current : [node];
    setNodesToDelete(nodes);
    onDeleteSelectedShapesModalOpen();
  }, []);

  const outsideOutline = isHoveredOutside
    ? `2px solid ${ChakraColor.blue300}`
    : 'none';

  const areAllSelectedShapes3d = selectedShapes.every(
    (shape) => shape.__typename === 'Shape'
  );
  const areAllSelectedShapes2d = selectedShapes.every(
    (shape) => shape.__typename === 'SheetShape'
  );
  const isSomeSelectedShapeAutoShape = selectedShapes.some(
    (shape) => shape.autoShapeId !== null
  );

  // Find the largest expanded folder by checking itemsToVirtualize
  const largestExpandedFolder = currentVirtualItems
    .filter((vi) => {
      const item = itemsToVirtualizeMap.get(String(vi.key));
      if (item && !isVirtualFolderNode(item.node)) {
        const nodeTypename = (
          item.node as ShapeFolderDeepFragment | AutoShapeDeepFragment
        ).__typename;
        if (nodeTypename === 'ShapeFolder' || nodeTypename === 'AutoShape') {
          return expandedPaths.has(item.node.name);
        }
      } else if (item && isVirtualFolderNode(item.node)) {
        const path = item.node.id.replace('virtual:', '');
        return expandedPaths.has(path);
      }
      return false;
    })
    .at(-1); // The assumption is that these are always in order

  // Find and remove all persisted folders that are not in virtual Items and appear after the largest expanded folder.
  for (const [k, v] of persistedVirtualFoldersRef.current) {
    const found = currentVirtualItems.find((vi) => vi.key === k);
    if (
      !found &&
      largestExpandedFolder &&
      v.start > largestExpandedFolder.start
    ) {
      persistedVirtualFoldersRef.current.delete(k);
    }
  }

  currentVirtualItems.forEach((item) => {
    const foundItem = itemsToVirtualizeMap.get(String(item.key));
    if (foundItem) {
      let isExpanded = false;
      if (isVirtualFolderNode(foundItem.node)) {
        const path = foundItem.node.id.replace('virtual:', '');
        isExpanded = expandedPaths.has(path);
      } else {
        const nodeTypename = (
          foundItem.node as ShapeFolderDeepFragment | AutoShapeDeepFragment
        ).__typename;
        if (nodeTypename === 'ShapeFolder' || nodeTypename === 'AutoShape') {
          isExpanded = expandedPaths.has(foundItem.node.name);
        }
      }

      if (isExpanded) {
        persistedVirtualFoldersRef.current.set(item.key, item);
      }
      if (!isExpanded && persistedVirtualFoldersRef.current.has(item.key)) {
        persistedVirtualFoldersRef.current.delete(item.key);
      }
    }
  });

  // Create a copy to avoid mutating state
  const persistedVirtualItems = [...currentVirtualItems];

  for (const [k, v] of persistedVirtualFoldersRef.current) {
    const found = persistedVirtualItems.find((vf) => vf.key === k);
    if (!found) {
      persistedVirtualItems.push(v);
    }
  }

  // Filter to get only folder/autoshape/virtual folder items
  const virtualFolders = persistedVirtualItems.filter((virtualItem) => {
    const foundItem = itemsToVirtualizeMap.get(String(virtualItem.key));
    if (!foundItem) return false;

    const nodeTypename = foundItem.node.__typename;
    return (
      nodeTypename === 'ShapeFolder' ||
      nodeTypename === 'AutoShape' ||
      nodeTypename === 'VirtualFolder'
    );
  });

  const virtualShapesWithoutFolders = currentVirtualItems.filter(
    (virtualItem) => {
      const found = shapesWithNoFolder.find(
        (shape) => shape.id === virtualItem.key
      );
      return !!found;
    }
  );

  const firstVirtualItemWithoutFolder = currentVirtualItems.find(
    (item) => item.key === shapesWithNoFolder[0]?.id
  );

  if (firstVirtualItemWithoutFolder) {
    firstShapeWithoutFolder.current = firstVirtualItemWithoutFolder;
  }

  const vShapeWithoutFoldersContainerTop = firstShapeWithoutFolder.current
    ?.start
    ? firstShapeWithoutFolder.current.start
    : 0;
  const shapeWithoutFoldersContainerHeight =
    SHAPE_ITEM_SIZE * shapesWithNoFolder.length;

  const isMovingOrCopying =
    isMovingShapes3D ||
    isCopyingShapes3D ||
    isMovingShapes2D ||
    isCopyingShapes2D;

  const modelUrn = useMemo(() => {
    return activeModels?.[0]?.forgeUrn ?? '';
  }, [activeModels]);

  const modelId = useMemo(() => {
    return activeModels?.[0]?.id ?? '';
  }, [activeModels]);

  const sheetId = useMemo(() => {
    return activeSheetId ?? '';
  }, [activeSheetId]);

  const pageNumber = useMemo(() => {
    return activeSheetPageNumber ?? 0;
  }, [activeSheetPageNumber]);

  const getShapeDiff = useCallback(
    // eslint-disable-next-line complexity
    (shape: ShapeDeepFragment | SheetShapeDeepFragment): ShapeItemDiff => {
      const isSheetShape = shape.__typename === 'SheetShape';
      const shapesKey = isSheetShape ? 'loadedSheetShapes' : 'loadedShapes';
      const nameKey = `propertyContext.${shapesKey}.${shape.urn}.${shape.id}.name`;
      const shapeKey = `propertyContext.${shapesKey}.${shape.urn}.${shape.id}`;
      const collectionKey = `propertyContext.${shapesKey}.${shape.urn}`;

      // Check for shape creation/deletion at collection level (first shape creation)
      if (diff?.[collectionKey]) {
        const collectionDiff = diff[collectionKey];
        if (
          collectionDiff.new &&
          typeof collectionDiff.new === 'object' &&
          collectionDiff.new[shape.id]
        ) {
          return {
            eventType: EventType.Created,
            oldName: undefined,
            newName: shape.name,
          };
        }
      }

      // Check for shape creation/deletion at individual level
      if (diff?.[shapeKey]) {
        const shapeDiff = diff[shapeKey];
        if (shapeDiff.new && !shapeDiff.old) {
          return {
            eventType: EventType.Created,
            oldName: undefined,
            newName: shape.name,
          };
        }
        if (shapeDiff.old && !shapeDiff.new) {
          return {
            eventType: EventType.Deleted,
            oldName: shape.name,
            newName: undefined,
          };
        }
      }

      // Check for name changes
      if (diff?.[nameKey]) {
        const nameDiff = diff[nameKey];
        return {
          eventType: EventType.Modified,
          oldName: nameDiff.old,
          newName: nameDiff.new,
        };
      }

      // Check for other shape property changes (non-name)
      if (diff) {
        const hasOtherChanges = Object.keys(diff).some(
          (key) => key.startsWith(shapeKey) && !key.endsWith('.name')
        );
        if (hasOtherChanges) {
          return {
            eventType: EventType.Modified,
            oldName: shape.name,
            newName: shape.name,
          };
        }
      }

      // Check for attribute changes
      const attributeDiff = getAttributeDiff(
        referenceState,
        diff,
        shape.urn,
        shape.dbId
      );
      if (attributeDiff) {
        const eventType = getEventType(attributeDiff);
        if (eventType) {
          return {
            eventType,
            oldName: shape.name,
            newName: shape.name,
          };
        }
      }

      // No changes - return current name
      return {
        oldName: shape.name,
        newName: shape.name,
      };
    },
    [diff, referenceState]
  );

  const getFolderDiff = useCallback(
    // eslint-disable-next-line complexity
    (
      folder: ShapeFolderDeepFragment | AutoShapeDeepFragment
    ): ShapeItemDiff => {
      // For shape folders, we need to use the project URN since folders don't have their own URN
      const projectUrn = getShapeUrn(projectId ?? '');
      const folderKey = `propertyContext.loadedShapeFolders.${projectUrn}.${folder.id}`;
      const nameKey = `propertyContext.loadedShapeFolders.${projectUrn}.${folder.id}.name`;
      const collectionKey = `propertyContext.loadedShapeFolders.${projectUrn}`;

      // 1. Check for folder creation/deletion at collection level (first folder creation)
      if (diff?.[collectionKey]) {
        const collectionDiff = diff[collectionKey];
        if (
          collectionDiff.new &&
          typeof collectionDiff.new === 'object' &&
          collectionDiff.new[folder.id]
        ) {
          // Folder created as part of collection replacement
          return {
            eventType: EventType.Created,
            oldName: undefined,
            newName: folder.name,
          };
        }
      }

      // 2. Check for folder creation/deletion at individual level
      if (diff?.[folderKey]) {
        const folderDiff = diff[folderKey];
        if (folderDiff.new && !folderDiff.old) {
          // Folder created
          return {
            eventType: EventType.Created,
            oldName: undefined,
            newName: folder.name,
          };
        }
        if (folderDiff.old && !folderDiff.new) {
          // Folder deleted
          return {
            eventType: EventType.Deleted,
            oldName: folder.name,
            newName: undefined,
          };
        }
      }

      // 3. Check for name changes
      if (diff?.[nameKey]) {
        const nameDiff = diff[nameKey];
        return {
          eventType: EventType.Modified,
          oldName: nameDiff.old,
          newName: nameDiff.new,
        };
      }

      // 4. Check for other folder property changes (non-name)
      if (diff) {
        const hasOtherChanges = Object.keys(diff).some(
          (key) => key.startsWith(folderKey) && !key.endsWith('.name')
        );
        if (hasOtherChanges) {
          return {
            eventType: EventType.Modified,
            oldName: folder.name,
            newName: folder.name,
          };
        }
      }

      // 5. Check if any shapes within the folder have been modified
      if (diff) {
        const shapesInFolder = shapes.filter(
          (shape) =>
            shape.folder === folder.id || shape.autoShapeId === folder.id
        );

        const hasShapeChanges = shapesInFolder.some((shape) => {
          const shapeDiff = getShapeDiff(shape);
          return shapeDiff.eventType !== undefined;
        });

        if (hasShapeChanges) {
          return {
            eventType: EventType.Modified,
            oldName: folder.name,
            newName: folder.name,
          };
        }
      }

      // 6. No changes - return current name
      return {
        oldName: folder.name,
        newName: folder.name,
      };
    },
    [diff, projectId, shapes, getShapeDiff]
  );

  return (
    <>
      <Panel
        resizeable
        panelContentRef={panelRef}
        setPanelHeight={setShapesPanelHeight}
      >
        <PanelHeader>
          <PanelTitle>{t('shapes')}</PanelTitle>
          <PanelActionButtons>
            {selectedShapes.length > 0 &&
            !readOnly &&
            !isMovingOrCopying &&
            (areAllSelectedShapes3d || areAllSelectedShapes2d) &&
            !isSomeSelectedShapeAutoShape ? (
              <>
                <Tooltip
                  label={t('project:shapes.move-shape')}
                  placement="top-start"
                >
                  <IconButton
                    icon={<SparkelIcon icon={faArrows} />}
                    colorScheme="gray"
                    aria-label="Move shape"
                    size="xs"
                    borderRadius="xl"
                    onClick={() =>
                      viewerMode === ViewerMode.Models
                        ? setMovingShapes3D(true)
                        : setMovingShapes2D(true)
                    }
                  />
                </Tooltip>
                <Tooltip
                  label={t('project:shapes.copy-shape')}
                  placement="top-start"
                >
                  <IconButton
                    icon={<SparkelIcon icon={faCopy} />}
                    colorScheme="gray"
                    aria-label="Copy shape"
                    size="xs"
                    borderRadius="xl"
                    onClick={() =>
                      viewerMode === ViewerMode.Models
                        ? setCopyingShapes3D(true)
                        : setCopyingShapes2D(true)
                    }
                  />
                </Tooltip>
              </>
            ) : null}
            {isMovingOrCopying && (
              <Tooltip label={t('project:shapes.cancel-drawing')}>
                <IconButton
                  aria-label="cancel moving/copying"
                  icon={<SparkelIcon icon={faTimes} color={'inherit'} />}
                  colorScheme="gray"
                  size={'xs'}
                  onClick={() => {
                    setCopyingShapes3D(false);
                    setMovingShapes3D(false);
                    setCopyingShapes2D(false);
                    setMovingShapes2D(false);
                  }}
                />
              </Tooltip>
            )}
            {isDrawing3d || isDrawingSheets ? (
              <>
                <Tooltip label={t('project:shapes.cancel-drawing')}>
                  <IconButton
                    aria-label="cancel shape"
                    icon={<SparkelIcon icon={faTimes} color={'inherit'} />}
                    colorScheme="gray"
                    size={'xs'}
                    onClick={() => {
                      if (viewerMode === ViewerMode.Models) {
                        console.log('deactivate3dDrawing');
                        deactivate3dDrawing();
                      } else {
                        console.log('deactivateSheetDrawing');
                        deactivateSheetDrawing();
                      }
                    }}
                  />
                </Tooltip>
                <Tooltip
                  label={
                    hasDrawingResult
                      ? t('project:shapes.save-tooltip')
                      : t('project:shapes.save-disabled-tooltip')
                  }
                >
                  <IconButton
                    aria-label="submit shape"
                    icon={<SparkelIcon icon={faCheck} color={'inherit'} />}
                    isDisabled={!hasDrawingResult}
                    size={'xs'}
                    onClick={() => {
                      if (viewerMode === ViewerMode.Models) {
                        submit3dDrawing();
                      } else {
                        submitSheetDrawing();
                      }
                    }}
                  />
                </Tooltip>
              </>
            ) : (
              !isMovingOrCopying &&
              !readOnly &&
              (isProtan ? (
                <Tooltip label={t('project:shapes.create')}>
                  <IconButton
                    icon={
                      <SparkelIcon
                        icon={faPlus}
                        color={['gray.50', 'gray.700']}
                      />
                    }
                    colorScheme="brand"
                    aria-label="New shape"
                    size="xs"
                    borderRadius="xl"
                    onClick={() => {
                      if (viewerMode === 'models') {
                        activate3dDrawing();
                      } else {
                        activateSheetDrawing();
                      }
                    }}
                  />
                </Tooltip>
              ) : (
                <ButtonGroup isAttached colorScheme="brand" variant={'outline'}>
                  <Tooltip label={t('project:shapes.create')}>
                    <Button
                      aria-label="New table modal"
                      size="xs"
                      borderRadius="md"
                      onClick={() => {
                        if (viewerMode === 'models') {
                          activate3dDrawing();
                        } else {
                          activateSheetDrawing();
                        }
                      }}
                    >
                      {t('project:shapes.button.add')}
                    </Button>
                  </Tooltip>
                  <Menu isLazy closeOnBlur={false}>
                    {({ onClose }) => (
                      <>
                        <MenuButton
                          as={IconButton}
                          size="xs"
                          borderRadius="md"
                          aria-label="add-shape-options"
                          icon={
                            <SparkelIcon
                              icon={faChevronDown}
                              color={'inherit'}
                            />
                          }
                        />
                        <Portal>
                          <AutoshapePopover
                            viewerMode={viewerMode}
                            createAutoShape={createAutoShape}
                            onClose={onClose}
                            findNextAvailableAutoShapeName={
                              findNextAvailableAutoShapeName
                            }
                            modelId={modelId}
                            modelUrn={modelUrn}
                            globalOffset={globalOffset ?? { x: 0, y: 0, z: 0 }}
                            sheetId={sheetId}
                            pageNumber={pageNumber}
                            templateRect={{
                              x: 100,
                              y: 100,
                              width: 50,
                              height: 50,
                            }}
                            sensitivity={0.5}
                            enableAutoShapes={enableAutoShapes}
                            onCreateGroup={createEmptyGroup}
                            activeFolderPath={activeFolderPath}
                          />
                        </Portal>
                      </>
                    )}
                  </Menu>
                </ButtonGroup>
              ))
            )}
          </PanelActionButtons>
        </PanelHeader>
        <PanelContent
          ref={panelRef}
          minHeight={6}
          maxHeight={'70vh'}
          paddingLeft={0}
          height={shapesPanelHeight}
          userSelect={'none'}
        >
          <Box
            height={`${shapeVirtualizer.getTotalSize()}px`}
            position="relative"
          >
            {folderHierarchy.length > 0 ? (
              <>
                {virtualFolders.map((virtualItem) => {
                  // Find the item info from itemsToVirtualize
                  const itemInfo = itemsToVirtualizeMap.get(
                    String(virtualItem.key)
                  );
                  return (
                    <ShapesFolder
                      key={virtualItem.key}
                      {...{
                        sortedFolders,
                        virtualItem,
                        shapesIndexedByFolder,
                        virtualItems: currentVirtualItems,
                        hiddenBIMElements,
                        hiddenSheetElements,
                        expandedPaths,
                        selectedFolder,
                        selectedShapes,
                        toggleFolderExpansion,
                        handleRename,
                        handleHide,
                        handleShow,
                        handleShapeMove,
                        handleNodeSelect,
                        onDeleteHandler,
                        changeActiveSheet,
                        edit3dShape,
                        editSheetShape,
                        setPageNumber,
                        setViewerMode,
                        ungroupShapesFromFolder,
                        deleteFolder,
                        groupShapesIntoFolder,
                        canGroupSelectedShapes,
                        selectedShapesRef,
                        selectedDbIds,
                        isDiffing: isHistoryDrawerOpen,
                        getShapeDiff,
                        getFolderDiff,
                        onMoveFolder: handleMoveFolder,
                        onMoveAutoShape: handleMoveAutoShape,
                        itemsToVirtualize,
                        level: itemInfo?.level ?? 0,
                        displayName: itemInfo?.displayName,
                        folderHierarchy,
                        renamingFolderId,
                        onRenameComplete: () => setRenamingFolderId(null),
                        ungroupFolder,
                      }}
                    />
                  );
                })}
                <Box
                  onDragOver={handleDragOverOutside}
                  onDrop={handleDropOutside}
                  onDragLeave={handleDragLeaveOutside}
                  outline={outsideOutline}
                  outlineOffset={-2}
                  width={'100%'}
                  borderRadius="md"
                  height={`${shapeWithoutFoldersContainerHeight}px`}
                  style={{ top: `${vShapeWithoutFoldersContainerTop}px` }}
                  position="relative"
                >
                  {virtualShapesWithoutFolders.map((virtualItem) => {
                    const shape = shapesWithNoFolder.find(
                      (shape) => shape.id === virtualItem.key
                    );
                    if (!shape) return null;
                    return (
                      <ShapeItem
                        isDiffing={isHistoryDrawerOpen}
                        diff={getShapeDiff(shape)}
                        key={virtualItem.key}
                        node={shape}
                        isSelected={isShapeSelected(shape, selectedDbIds)}
                        onShapeSelect={handleNodeSelect}
                        onMoveNode={handleShapeMove}
                        onRename={handleRename}
                        onShow={handleShow}
                        onHide={handleHide}
                        onDelete={onDeleteHandler}
                        isHidden={isShapeHidden(
                          shape,
                          hiddenBIMElements,
                          hiddenSheetElements,
                          false,
                          autoHiddenLegends
                        )}
                        changeActiveSheet={changeActiveSheet}
                        editBIMShape={edit3dShape}
                        setPageNumber={setPageNumber}
                        editSheetShape={editSheetShape}
                        setViewerMode={setViewerMode}
                        removeShapesFromFolder={ungroupShapesFromFolder}
                        deleteFolder={deleteFolder}
                        groupShapesIntoFolder={groupShapesIntoFolder}
                        canGroupSelectedShapes={canGroupSelectedShapes}
                        selectedShapesRef={selectedShapesRef}
                        top={
                          virtualItem.start - vShapeWithoutFoldersContainerTop
                        }
                        height={virtualItem.size}
                      />
                    );
                  })}
                </Box>
              </>
            ) : shapes.length > 0 ? (
              currentVirtualItems.map((virtualItem) => {
                const shape = sortedShapes.find(
                  (sshape) => sshape.id === virtualItem.key
                );
                if (!shape) return null;
                return (
                  <ShapeItem
                    isDiffing={isHistoryDrawerOpen}
                    diff={getShapeDiff(shape)}
                    key={virtualItem.key}
                    node={shape}
                    isSelected={selectedShapes.some((s) => s.id === shape.id)}
                    onShapeSelect={handleNodeSelect}
                    onMoveNode={handleShapeMove}
                    onRename={handleRename}
                    onShow={handleShow}
                    onHide={handleHide}
                    onDelete={onDeleteHandler}
                    isHidden={isShapeHidden(
                      shape,
                      hiddenBIMElements,
                      hiddenSheetElements,
                      false,
                      autoHiddenLegends
                    )}
                    changeActiveSheet={changeActiveSheet}
                    editBIMShape={edit3dShape}
                    setPageNumber={setPageNumber}
                    editSheetShape={editSheetShape}
                    setViewerMode={setViewerMode}
                    removeShapesFromFolder={ungroupShapesFromFolder}
                    deleteFolder={deleteFolder}
                    groupShapesIntoFolder={groupShapesIntoFolder}
                    canGroupSelectedShapes={canGroupSelectedShapes}
                    selectedShapesRef={selectedShapesRef}
                    top={virtualItem.start}
                    height={virtualItem.size}
                  />
                );
              })
            ) : (
              <PlaceholderText margin={2}>No shapes created</PlaceholderText>
            )}
          </Box>
        </PanelContent>
      </Panel>
      <ConfirmDeletionModal
        onConfirm={onDeleteConfirm}
        isOpen={showDeleteSelectedShapesModal}
        onClose={onDeleteSelectedShapesModalClose}
        itemsToDelete={nodesToDelete.map((e) => e.name)}
      />
      {/* Rename Popover anchored at cursor position */}
      <Popover
        isOpen={isRenamePopoverOpen}
        onClose={closeRenamePopover}
        autoFocus={false}
        placement="bottom-start"
        isLazy
      >
        <PopoverAnchor>
          <Box
            position="fixed"
            pointerEvents="none"
            style={{
              left: renameAnchorPos?.x ?? -9999,
              top: renameAnchorPos?.y ?? -9999,
            }}
          />
        </PopoverAnchor>
        <Portal>
          <PopoverContent width="xs" p={1} zIndex={9999} borderRadius="xl">
            <PopoverBody p={1}>
              <HStack>
                <Input
                  ref={renameInputRef}
                  size="sm"
                  variant={'unstyled'}
                  borderRadius={0}
                  value={renameInput}
                  onChange={(e) => setRenameInput(e.target.value)}
                  onFocus={(e) => e.currentTarget.select()}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      closeRenamePopover();
                    }
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (renameTarget) {
                        const trimmed = renameInput.trim();
                        if (!trimmed || trimmed === renameTarget.name) {
                          closeRenamePopover();
                        } else {
                          void handleRename(renameTarget, trimmed).then(() => {
                            closeRenamePopover();
                          });
                        }
                      }
                    }
                  }}
                />
                <IconButton
                  aria-label={t('common:cancel')}
                  size="xs"
                  colorScheme="gray"
                  icon={<SparkelIcon icon={faTimes} color={'inherit'} />}
                  onClick={closeRenamePopover}
                />
                <IconButton
                  aria-label={t('common:save')}
                  size="xs"
                  icon={<SparkelIcon icon={faCheck} color={'inherit'} />}
                  onClick={() => {
                    if (renameTarget) {
                      const trimmed = renameInput.trim();
                      if (!trimmed || trimmed === renameTarget.name) {
                        closeRenamePopover();
                      } else {
                        void handleRename(renameTarget, trimmed).then(() => {
                          closeRenamePopover();
                        });
                      }
                    }
                  }}
                />
              </HStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </>
  );
}

export const isShapeSelected = (
  shape: ShapeDeepFragment | SheetShapeDeepFragment,
  selectedDbIds: Record<string, number[]>
) => {
  const shapeUrn = shape.urn;
  return (
    shapeUrn in selectedDbIds && selectedDbIds[shapeUrn].includes(shape.dbId)
  );
};

export const isShapeHidden = (
  node: ShapeNodeType,
  hiddenBimElements: DbIdsPerModel,
  hiddenSheetElements: DbIdsPerModel,
  allChildrenHidden = false,
  autoHiddenLegends: DbIdsPerModel = {}
) => {
  if (allChildrenHidden) return true;
  if (node.__typename === 'Shape' && hiddenBimElements[node.urn]) {
    return hiddenBimElements[node.urn].includes(node.dbId);
  }
  if (node.__typename === 'SheetShape') {
    // Check if auto-hidden (legends for non-active orders)
    if (autoHiddenLegends[node.urn]?.includes(node.dbId)) {
      return true;
    }
    // Check if manually hidden
    if (hiddenSheetElements[node.urn]?.includes(node.dbId)) {
      return true;
    }
  }
  return false;
};

export default memo(ShapesPanel);

const compareShapeNameWithNumber = (a: string, b: string) => {
  // Check if the strings end with a number
  const regex = /(\d+)$/;
  const aMatch = a.trim().match(regex);
  const bMatch = b.trim().match(regex);

  // If both strings end with numbers, compare numerically
  if (aMatch && bMatch) {
    const aNumber = parseInt(aMatch[1], 10);
    const bNumber = parseInt(bMatch[1], 10);
    return aNumber - bNumber;
  }

  // If only one string ends with a number, prioritize the one without
  if (aMatch && !bMatch) return 1;
  if (!aMatch && bMatch) return -1;

  // If neither string ends with a number, compare alphabetically
  return a.localeCompare(b);
};
